import {
  BACKTESTING_STATUS_TO_COLOR,
  BACKTESTING_STATUS_TO_DISPLAY_NAME,
} from "@shared/features/bots/BotForm/common/constants";
import {
  BacktestingRunType,
  BacktestingStatus,
  LanguageNameSpaces,
} from "@shared/types";
import StatusTag from "common/Tags/StatusTag";
import Tooltip from "common/Tooltips/Tooltip";
import { LoaderCircle } from "lucide-react";
import React from "react";
import styled, { keyframes, useTheme } from "styled-components";
import { hexToRGB } from "@shared/utils/misc";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import useLiveBacktesting from "@shared/features/bots/BotForm/Backtesting/useLiveBacktesting";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types";

interface BacktestStatusTagProps {
  backtestingRun?: BacktestingRunType;
}

const BacktestStatusTag = ({ backtestingRun }: BacktestStatusTagProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const theme = useTheme();
  const { status } = useLiveBacktesting({ data: backtestingRun });
  const isFailed = status === BacktestingStatus.FAILED;

  if (theme.isNewLook && status) {
    // New look: a quiet dot + word (no tinted pill). A completed run stays
    // calm; a running one spins; a failed one reads in red, with the error
    // on hover.
    const tokens = getNewLookTokens(theme);
    const isInProgress = status === BacktestingStatus.IN_PROGRESS;
    const color = isInProgress
      ? tokens.accent
      : isFailed
      ? tokens.down
      : tokens.up;
    const label = (
      <NewLookStatus
        $color={isFailed ? tokens.down : undefined}
        data-status={status}
      >
        {isInProgress ? (
          <Spinner size={13} strokeWidth={2.25} color={color} />
        ) : (
          <Dot style={{ background: color }} $color={color} />
        )}
        {tCommon(BACKTESTING_STATUS_TO_DISPLAY_NAME[status])}
      </NewLookStatus>
    );
    if (!isFailed) return label;
    return (
      <Tooltip
        title={t("backtesting.messages.backtest_error")}
        showMobileBottomDrawer={true}
        mobileTitle={t("backtesting.messages.backtest_failed")}
      >
        {label}
      </Tooltip>
    );
  }

  return (
    <>
      {status && (
        <StatusTag
          text={tCommon(BACKTESTING_STATUS_TO_DISPLAY_NAME[status])}
          color={theme.colors[BACKTESTING_STATUS_TO_COLOR[status]]}
          showCircle={status === BacktestingStatus.IN_PROGRESS}
          style={{
            padding: "2px 6px",
            borderRadius: 6,
            width: "fit-content",
          }}
          tooltipContent={
            isFailed ? t("backtesting.messages.backtest_error") : undefined
          }
          mobileTooltipTitle={
            isFailed ? t("backtesting.messages.backtest_failed") : undefined
          }
        />
      )}
    </>
  );
};

export default BacktestStatusTag;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;
const NewLookStatus = styled.span<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  ${newLookType.label};
  color: ${(p) => p.$color ?? getNewLookTokens(p.theme).secondary};
  white-space: nowrap;
`;
const Dot = styled.span<{ $color: string }>`
  width: 7px;
  height: 7px;
  flex: none;
  border-radius: 50%;
  box-shadow: 0 0 0 3px ${(p) => hexToRGB(p.$color, 0.16)};
`;
const Spinner = styled(LoaderCircle)`
  flex: none;
  animation: ${spin} 1.2s linear infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
